import {
  Avatar,
  IconButton,
  Popover,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { ITherapySuppliersResult } from 'API/Suppliers/suppliers';
import { SuppliersContext } from 'Context/SuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import {
  BlueRequest,
  PurpleFillShareMedia,
  PurpleIcon,
  ShareMediaPurple,
  WhatsAppIcon,
} from 'Images';
import {
  ISupplier,
  getSupplierResource,
  supplierResourceFilterMapper,
} from 'Utils/suppliersResources';
import React, { useContext, useState } from 'react';

interface IButtonShare {
  functionHandler: Function;
  icon: string;
  text: string;
}
interface IShare {
  data: ITherapySuppliersResult;
}

export const Share = ({ data }: IShare) => {
  const { resources, expertise } = useContext(SuppliersContext);
  const [isMediaPopoverOpen, setIsMediaPopoverOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const isDesktop = useIsDesktop();

  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage
    .therapy;

  const openMediaPopoverHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as Element);
    setIsMediaPopoverOpen(!isMediaPopoverOpen);
  };

  const supplierPageURL = `${window.location.href}/${data.professions[0]}/${data.id}`;

  const supplierSpecialties = supplierResourceFilterMapper(
    expertise as ISupplier,
    data.specialties
  );

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const findCity = (cityId: number | undefined) => {
    if (!cityId) return '';
    const city = cities?.find((city: any) => city.id === cityId)?.value;
    return city ? `, ${city}` : '';
  };

  const mediaStackSx: SxProps = {
    flexFlow: 'row',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    p: '8px 24px 8px 16px',

    ':hover': {
      cursor: 'pointer',
      bgcolor: 'purple2.shade_100',
    },
  };

  const mediaIconSx: SxProps = {
    width: '20px',
    height: '20px',
  };

  const buildShareMediaText = () => {
    let text: string = `${dictionary.foundSupplier}\n`;

    text += `${data.name} | ` || '';
    text += `${getSupplierResource(resources.OCCUP, data.professions[0])}`;

    if (supplierSpecialties.length > 0) {
      text += ` - ${
        Dictionary[DICTIONARY.Suppliers].resultPage.therapy.speciality
      } `;
      supplierSpecialties.map((specialty: string, index: number) => {
        return (text += `${specialty}${
          index < supplierSpecialties.length - 1 ? ', ' : ''
        }`);
      });
    }
    text += '\n\n';

    text += data.firstStreet || '';
    text += findCity(data.firstCityId) || '';
    text += data.secondStreet ? `\n${data.secondStreet}` : '';
    text += findCity(data.secondCityId) || '';

    text += data.firstStreet || data.secondStreet ? '\n\n' : '';

    text +=
      data.homePhonePrefix && data.homePhoneNumber
        ? `${dictionary.telephone}: ${data.homePhonePrefix}-${data.homePhoneNumber}`
        : '';
    text +=
      data.cellPhonePrefix && data.cellPhoneNumber
        ? `\n${dictionary.cellphone}: ${data.cellPhonePrefix}-${data.cellPhoneNumber}`
        : '';

    text +=
      (data.homePhonePrefix && data.homePhoneNumber) ||
      (data.cellPhonePrefix && data.cellPhoneNumber)
        ? '\n\n'
        : '';
    text += `${dictionary.fullSupplierInfo}\n`;

    text += supplierPageURL;
    return text;
  };

  const clipboardCopyHandler = () => {
    navigator.clipboard.writeText(supplierPageURL).then(() => {
      alert(dictionary.urlCopied);
    });
  };

  const emailShareHandler = () => {
    const subject = encodeURIComponent(dictionary.foundSupplier);
    const body = encodeURIComponent(buildShareMediaText());
    const emailLink = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = emailLink;
  };

  const whatsAppShareHandler = async () => {
    const text = encodeURIComponent(buildShareMediaText());
    const webWhatsAppLink = isDesktop
      ? `https://web.whatsapp.com/send?text=${text}`
      : `whatsapp://send?text=${text}`;
    window.open(webWhatsAppLink, '_blank');
  };

  const shareButtons: IButtonShare[] = [
    {
      functionHandler: clipboardCopyHandler,
      icon: PurpleIcon,
      text: dictionary.copy,
    },
    {
      functionHandler: emailShareHandler,
      icon: BlueRequest,
      text: dictionary.mailShare,
    },
    {
      functionHandler: whatsAppShareHandler,
      icon: WhatsAppIcon,
      text: dictionary.whatsAppShare,
    },
  ];

  return (
    <IconButton
      onClick={openMediaPopoverHandler}
      sx={{
        p: 0,
        ':hover': {
          outline: '1px solid #A020FF',
        },
      }}
      aria-haspopup={true}
      aria-label={`${dictionary.ariaShareButton} ${data.name}`}
    >
      <Avatar
        alt=''
        src={isMediaPopoverOpen ? PurpleFillShareMedia : ShareMediaPurple}
        sx={
          isDesktop
            ? { width: '32px', height: '32px' }
            : { width: '26px', height: '26px' }
        }
      />
      <Popover
        open={isMediaPopoverOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            display: 'flex',
            flexFlow: 'column',
            mt: isDesktop ? '7px' : '3px',
            boxShadow: 'none',
            border: '1px solid #A020FF',
          },
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === `Escape`) {
            setAnchorEl(event.currentTarget as Element);
            setIsMediaPopoverOpen(!isMediaPopoverOpen);
          }
        }}
        aria-live='assertive'
      >
        {shareButtons.map((shareBtn: IButtonShare, index: number) => {
          return (
            <Stack
              key={index}
              sx={{ ...mediaStackSx }}
              onClick={() => shareBtn.functionHandler()}
              role='button'
              tabIndex={0}
            >
              <Avatar alt='' src={shareBtn.icon} sx={{ ...mediaIconSx }} />
              <Typography variant='body6'>{shareBtn.text}</Typography>
            </Stack>
          );
        })}
      </Popover>
    </IconButton>
  );
};
